// Display formatting (I18N-004): viewer timezone and locale aware.
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz'

let viewerTimeZone: string | null = null
let formatLocale = 'en'
type Translate = (text: string, vars?: Record<string, string | number>) => string
let translate: Translate = (text, vars) => (vars ? text.replace(/\{(\w+)\}/g, (whole, k: string) => String(vars[k] ?? whole)) : text)

/** i18n.ts installs t() here so the few words in this file follow the UI language. */
export function setFormatTranslator(fn: Translate): void {
  translate = fn
}

/** Browser timezone, falling back to UTC. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/** Set from me.user.timezone (auth.ts). Null resets to the browser zone. */
export function setViewerTimeZone(tz: string | null | undefined): void {
  viewerTimeZone = tz && isValidTimeZone(tz) ? tz : null
}

export function getViewerTimeZone(): string {
  return viewerTimeZone ?? browserTimeZone()
}

/** Set from the active UI language (i18n.ts). */
export function setFormatLocale(locale: string): void {
  formatLocale = locale || 'en'
}

export function getFormatLocale(): string {
  return formatLocale
}

export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: tz })
    return true
  } catch {
    return false
  }
}

export type DateInput = Date | string | number

export function toDate(value: DateInput): Date {
  return value instanceof Date ? value : new Date(value)
}

function dtf(opts: Intl.DateTimeFormatOptions, timeZone = getViewerTimeZone()): Intl.DateTimeFormat {
  try {
    return new Intl.DateTimeFormat(formatLocale, { timeZone, ...opts })
  } catch {
    return new Intl.DateTimeFormat('en', { timeZone: 'UTC', ...opts })
  }
}

function zonedYear(d: Date): number {
  return Number(dtf({ year: 'numeric' }).formatToParts(d).find((p) => p.type === 'year')?.value ?? d.getFullYear())
}

/**
 * Compact relative time: "now", "5m", "2h", "3d", then "Sep 12" this year and "Sep 12, 2025" before.
 * Future dates (clock skew) read as "now".
 */
export function timeAgo(value: DateInput, now: DateInput = Date.now()): string {
  const d = toDate(value)
  const ms = toDate(now).getTime() - d.getTime()
  if (Number.isNaN(ms)) return ''
  const s = Math.floor(ms / 1000)
  if (s < 60) return translate('now')
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h`
  const days = Math.floor(h / 24)
  if (days < 7) return `${days}d`
  if (zonedYear(d) === zonedYear(toDate(now))) return dtf({ month: 'short', day: 'numeric' }).format(d)
  return dtf({ month: 'short', day: 'numeric', year: 'numeric' }).format(d)
}

/**
 * timeAgo worded to follow a verb in a sentence ("Created", "Last used", "Last attempt failed"): "just now",
 * "5m ago", "2h ago", "3d ago", then "on Sep 12" or "on Sep 12, 2025" (Q-SRCH-37).
 */
export function timeAgoPhrase(value: DateInput, now: DateInput = Date.now()): string {
  const d = toDate(value)
  const ms = toDate(now).getTime() - d.getTime()
  if (Number.isNaN(ms)) return ''
  if (ms < 60_000) return translate('just now')
  const label = timeAgo(d, now)
  if (ms < 7 * 24 * 3_600_000) return translate('{time} ago', { time: label })
  return translate('on {date}', { date: label })
}

/** English copy writes "9:51 pm" (UI.md 9): lowercases the day period, other languages keep theirs. */
function formatLowerDayPeriod(fmt: Intl.DateTimeFormat, d: Date): string {
  if (!formatLocale.toLowerCase().startsWith('en')) return fmt.format(d)
  return fmt
    .formatToParts(d)
    .map((p) => (p.type === 'dayPeriod' ? p.value.toLowerCase() : p.value))
    .join('')
}

/** Full local date and time, for tooltips: "Sep 12, 2026, 3:04 pm". */
export function formatDateTime(value: DateInput, opts: Intl.DateTimeFormatOptions = {}): string {
  const d = toDate(value)
  if (Number.isNaN(d.getTime())) return ''
  return formatLowerDayPeriod(dtf({ dateStyle: 'medium', timeStyle: 'short', ...opts }), d)
}

/** Date only: "Sep 12, 2026" (pass opts to change the style). */
export function formatDate(value: DateInput, opts: Intl.DateTimeFormatOptions = { dateStyle: 'medium' }): string {
  const d = toDate(value)
  if (Number.isNaN(d.getTime())) return ''
  return dtf(opts).format(d)
}

/** Time only: "3:04 pm". */
export function formatTime(value: DateInput, opts: Intl.DateTimeFormatOptions = { timeStyle: 'short' }): string {
  const d = toDate(value)
  if (Number.isNaN(d.getTime())) return ''
  return formatLowerDayPeriod(dtf(opts), d)
}

/** date-fns pattern in the viewer timezone, e.g. formatInViewerTz(d, "yyyy-MM-dd"). */
export function formatInViewerTz(value: DateInput, pattern: string, timeZone = getViewerTimeZone()): string {
  return formatInTimeZone(toDate(value), timeZone, pattern)
}

/** Wall clock Date in the viewer timezone (for calendar grids). */
export function toViewerZoned(value: DateInput, timeZone = getViewerTimeZone()): Date {
  return toZonedTime(toDate(value), timeZone)
}

/** Converts a wall clock time picked in the viewer timezone back to a real instant. */
export function fromViewerZoned(value: DateInput, timeZone = getViewerTimeZone()): Date {
  return fromZonedTime(toDate(value), timeZone)
}

function nf(opts: Intl.NumberFormatOptions = {}): Intl.NumberFormat {
  try {
    return new Intl.NumberFormat(formatLocale, opts)
  } catch {
    return new Intl.NumberFormat('en', opts)
  }
}

/** Counts: 999, 1,234, 12.3k, 123k, 1.2M, 12M. */
export function formatCount(n: number | null | undefined): string {
  const v = Number(n ?? 0)
  if (!Number.isFinite(v)) return '0'
  const abs = Math.abs(v)
  if (abs < 10_000) return nf({ maximumFractionDigits: 0 }).format(v)
  if (abs < 1_000_000) {
    const k = v / 1000
    const digits = Math.abs(k) < 100 ? 1 : 0
    return nf({ maximumFractionDigits: digits }).format(truncate(k, digits)) + 'k'
  }
  const m = v / 1_000_000
  const digits = Math.abs(m) < 100 ? 1 : 0
  return nf({ maximumFractionDigits: digits }).format(truncate(m, digits)) + 'M'
}

/** Rounds toward zero so 12,399 reads 12.3k (never more than the real count). */
function truncate(x: number, digits: number): number {
  const f = 10 ** digits
  return Math.trunc(x * f) / f
}

/** Money in minor units: formatMoney(4900, "usd") = "$49", formatMoney(999, "eur") = "€9.99". */
export function formatMoney(amountCents: number, currency = 'usd', opts: { forceDecimals?: boolean } = {}): string {
  const cents = Math.round(Number(amountCents) || 0)
  const whole = cents % 100 === 0 && !opts.forceDecimals
  try {
    return nf({
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    }).format(cents / 100)
  } catch {
    return `${(cents / 100).toFixed(whole ? 0 : 2)} ${currency.toUpperCase()}`
  }
}

/** Video length: 75 = "1:15", 3725 = "1:02:05". */
export function formatDuration(totalSeconds: number | null | undefined): string {
  const t = Math.max(0, Math.floor(Number(totalSeconds) || 0))
  const h = Math.floor(t / 3600)
  const m = Math.floor((t % 3600) / 60)
  const s = t % 60
  const ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`
}

/** File sizes: 512 B, 2.4 KB, 3.1 MB, 1.2 GB. */
export function formatBytes(bytes: number | null | undefined): string {
  const b = Math.max(0, Number(bytes) || 0)
  if (b < 1024) return `${b} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let v = b / 1024
  let i = 0
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i++
  }
  return `${nf({ maximumFractionDigits: v < 10 ? 1 : 0 }).format(v)} ${units[i]}`
}

// English short names that read with "the" in a sentence ("in Canada and the United States"). Other languages use
// Intl.DisplayNames as it is; their sentences put the list after a colon, so no article or case is needed.
const ENGLISH_THE = new Set(['US', 'GB', 'NL', 'AE', 'PH', 'BS', 'GM', 'DO', 'CF', 'KM', 'MV', 'MH', 'SB', 'CD', 'CG', 'VA', 'KY', 'VG', 'VI', 'TC', 'FK', 'FO'])

/** A country's name for an ISO 3166 alpha 2 code in the UI language ("CA" -> "Canada", "Kanada" in German); an
 *  unknown code stays as it is. */
export function formatCountry(code: string, locale = formatLocale): string {
  const upper = code.trim().toUpperCase()
  let name = upper
  try {
    name = new Intl.DisplayNames([locale, 'en'], { type: 'region' }).of(upper) ?? upper
  } catch {
    /* an old browser without DisplayNames: the code */
  }
  return locale.toLowerCase().startsWith('en') && ENGLISH_THE.has(upper) && name !== upper ? `the ${name}` : name
}

/** "Canada and the United States" in the UI language (Intl.ListFormat, a comma list without it). */
export function formatCountryList(codes: readonly string[], locale = formatLocale): string {
  const names = codes.map((c) => formatCountry(c, locale))
  try {
    return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(names)
  } catch {
    return names.join(', ')
  }
}
